// Los impresos de la OD con el formato EXACTO de Random (pedido de Rodrigo, 2026-10-13: «lo quiero
// igual al de Random»): un reporte de texto de ancho fijo, como los que imprime Random, con su
// cabecera, sus líneas de guiones y sus mismas columnas y rótulos. Aquí solo se arma el texto; el
// componente lo dibuja en monoespaciado.
import type { DocumentosNotaOd, Od, ProductoManifiesto } from './datos';

export interface LineaReporte {
  texto: string;
  /** El título del reporte va en negrita y subrayado, como en Random. */
  titulo?: boolean;
  /** Línea de detalle (un producto): sin negrita (pedido de Rodrigo, 2026-10-21). */
  detalle?: boolean;
  /**
   * Manifiesto (2026-10-23): el producto más oscuro y la cantidad en negrita, con una línea suave de
   * puntos bajo cada producto para no confundir renglones al cargar. `texto` sigue siendo la línea.
   */
  partes?: { producto: string; cantidad: string };
}

// Angosto para que la letra salga grande en una hoja vertical (pedido de Rodrigo, 2026-10-14).
const ANCHO_MANIFIESTO = 96;
// Horizontal con letra grande (pedido de Rodrigo, 2026-10-14).
const ANCHO_RENDICION = 160;

/** «1.234,50»: miles con punto y dos decimales con coma (como Random). */
export const dosDecimales = (v: number) =>
  v.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const miles = (v: number) => Math.round(v).toLocaleString('es-CL');
export const izq = (s: string, n: number) => (s.length > n ? s.slice(0, n) : s.padEnd(n));
export const der = (s: string, n: number) => (s.length > n ? s.slice(-n) : s.padStart(n));
export const centro = (s: string, ancho: number) =>
  ' '.repeat(Math.max(0, Math.floor((ancho - s.length) / 2))) + s;
export const ddmmaaaa = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};
export const rutEmpresa = (rut: string, dv: string) =>
  `${Number(rut).toLocaleString('es-CL')}-${dv}`;

/** Cabecera común: empresa y RUT a la izquierda, fecha y página a la derecha, título al centro. */
function cabecera(
  od: Od,
  empresa: { razonSocial: string; rut: string; dv: string },
  bodega: string,
  ancho: number,
): LineaReporte[] {
  const hoy = ddmmaaaa(new Date().toLocaleDateString('sv-SE'));
  const derecha = (a: string, b: string) => ({ texto: izq(a, ancho - b.length) + b });
  const hora = new Date(od.creadaEn).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const camion = od.patente
    ? `${od.patente} ${od.vehiculoDescripcion ?? ''}`.trim().toUpperCase()
    : '';
  return [
    derecha(empresa.razonSocial.toUpperCase(), `Fecha :${hoy}`),
    derecha(`R.U.T.:${rutEmpresa(empresa.rut, empresa.dv)}`, 'Pagina : 000001'),
    {
      texto: centro(
        `DOCUMENTACION DE LA ORDEN DE DESPACHO NUMERO : #${String(od.numero).padStart(9, '0')}`,
        ancho,
      ),
      titulo: true,
    },
    {
      texto: centro(
        `Fecha registro : ${ddmmaaaa(od.fechaNegocio)} Fecha de cierre   /  /  `,
        ancho,
      ),
    },
    { texto: centro(`Hora de creación : ${hora}`, ancho) },
    {
      texto: centro(
        `Bodega ${bodega}${camion ? ` CAMION ${camion}` : ''} Encargado: ${(od.chofer ?? '').toUpperCase()}`,
        ancho,
      ),
    },
    { texto: '' },
  ];
}

/**
 * Manifiesto de carga: como «DOCUMENTACION DE LA ORDEN DE DESPACHO» de Random. Por súper familia,
 * cada producto con su cantidad en la unidad base (1) y en cajas (2), subtotales, totales por unidad,
 * kilos, NV y clientes.
 */
export function textoManifiesto(
  od: Od,
  productos: ProductoManifiesto[],
  resumen: { kilos: string; notas: number; clientes: number } | undefined,
  empresa: { razonSocial: string; rut: string; dv: string },
  bodega: string,
): LineaReporte[] {
  const A = ANCHO_MANIFIESTO;
  const raya = '-'.repeat(A);
  const notas = od.notas.filter((n) => n.estado !== 'RETIRADA');
  const l: LineaReporte[] = cabecera(od, empresa, bodega, A);
  l.push({ texto: '_'.repeat(A) });
  l.push({ texto: `Composición de la Orden de Despacho Ref : ${(od.ruta ?? '').toUpperCase()}` });
  l.push({
    texto: `${izq('Código', 10)}${izq('Descripción', 38)}${der('-Cantidad(1) UD', 17)}${der(' -Cantidad(2) UD', 16)}`,
  });
  l.push({ texto: '_'.repeat(A) });
  l.push({ texto: ' ' });
  // Los documentos en tantas líneas como hagan falta (no se salen del ancho).
  let linea = 'Documentos :';
  for (const n of notas) {
    if (linea.length + 1 + n.numeroVisible.length > A) {
      l.push({ texto: linea });
      linea = ' '.repeat(12);
    }
    linea += ` ${n.numeroVisible}`;
  }
  l.push({ texto: linea });
  const grupos = new Map<string, { nombre: string; productos: ProductoManifiesto[] }>();
  for (const p of productos) {
    const clave = p.superFamilia?.codigo ?? '~';
    const g = grupos.get(clave) ?? {
      nombre: p.superFamilia ? `${p.superFamilia.codigo} ${p.superFamilia.nombre}` : 'SIN FAMILIA',
      productos: [],
    };
    g.productos.push(p);
    grupos.set(clave, g);
  }
  let totalUd1 = 0;
  let totalUd2 = 0;
  const porUnidad = new Map<string, number>();
  for (const [, g] of [...grupos.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    l.push({ texto: `Super Familia : ${g.nombre.toUpperCase()}` });
    l.push({ texto: raya });
    let sub1 = 0;
    let sub2 = 0;
    for (const p of [...g.productos].sort((a, b) => a.descripcion.localeCompare(b.descripcion))) {
      const un = Number(p.total);
      const factor = p.caja && Number(p.caja.factor) > 1 ? Number(p.caja.factor) : null;
      const ud2 = factor ? p.caja!.unidad : (p.unidadBase ?? 'UN');
      const cajas = factor ? un / factor : un;
      const enteras = factor ? Math.floor(cajas + 1e-9) : 0;
      const sueltas = factor ? Math.round((un - enteras * factor) * 100) / 100 : 0;
      const detalle = factor
        ? `(${[enteras ? `${enteras} ${ud2}` : '', sueltas ? `${dosDecimales(sueltas)} UN` : '']
            .filter(Boolean)
            .join(' + ')})`
        : '';
      const producto = `${izq(p.sku, 10)}${izq(p.descripcion.toUpperCase(), 38)}`;
      const cantidad =
        `${der(dosDecimales(un), 13)} ` +
        `${izq(p.unidadBase ?? 'UN', 3)}${der(dosDecimales(cajas), 11)} ${izq(ud2, 3)} ${detalle}`;
      l.push({ detalle: true, texto: producto + cantidad, partes: { producto, cantidad } });
      sub1 += un;
      sub2 += cajas;
      porUnidad.set(ud2, (porUnidad.get(ud2) ?? 0) + cajas);
    }
    l.push({ texto: raya });
    l.push({
      texto: `${izq('', 36)}${izq('Totales :', 12)}${der(dosDecimales(sub1), 13)}    ${der(dosDecimales(sub2), 11)}`,
    });
    l.push({ texto: ' ' });
    totalUd1 += sub1;
    totalUd2 += sub2;
  }
  const unidades = ['UN', ...[...porUnidad.keys()].filter((u) => u !== 'UN').sort()];
  l.push({ texto: raya });
  l.push({
    texto:
      ' '.repeat(36) +
      'Totales por unidad ' +
      unidades.map((u) => `${u} ${der(dosDecimales(porUnidad.get(u) ?? 0), 10)}`).join('  '),
  });
  l.push({ texto: raya });
  const total = (rotulo: string, valor: string, segundo = '') => ({
    texto: `${' '.repeat(36)}${izq(rotulo, 20)}:${der(valor, 12)}${segundo ? der(segundo, 15) : ''}`,
  });
  l.push(total('Kilos Totales', resumen ? dosDecimales(Number(resumen.kilos)) : ''));
  l.push(total('Totales (ud1/ud2)', dosDecimales(totalUd1), dosDecimales(totalUd2)));
  l.push(total('NVV Totales', String(resumen?.notas ?? notas.length)));
  l.push(
    total(
      'Clientes',
      String(resumen?.clientes ?? new Set(notas.map((n) => n.clienteEntidadId)).size),
    ),
  );
  return l;
}

/** El tipo de documento como lo escribe Random. */
const TD_RANDOM: Record<number, string> = { 33: 'FCV', 34: 'FEV', 39: 'BLV', 41: 'BEV', 52: 'GDV' };

/**
 * Hoja de rendición: la «Composición de la Orden de Despacho» de Random, un renglón por documento con
 * su monto y condición de pago, y los espacios punteados para efectivo, cheque y observaciones.
 */
export function textoRendicion(
  od: Od,
  documentos: DocumentosNotaOd[],
  empresa: { razonSocial: string; rut: string; dv: string },
  bodega: string,
): LineaReporte[] {
  const A = ANCHO_RENDICION;
  const raya = '-'.repeat(A);
  const notas = od.notas.filter((n) => n.estado !== 'RETIRADA');
  const docs = new Map(documentos.map((d) => [d.notaId, d.documentos]));
  const l: LineaReporte[] = cabecera(od, empresa, bodega, A);
  l.push({ texto: '_'.repeat(A) });
  l.push({ texto: `Composición de la Orden de Despacho Ref : ${(od.ruta ?? '').toUpperCase()}` });
  l.push({
    texto:
      `${izq('TD', 4)}${izq('Numero', 11)}${izq('Entidad', 11)}${izq('Cliente', 30)}${izq('Sucursal', 22)}` +
      `${der('-----Monto', 11)} ## 1er.Venc Ult.Venc ${izq('Condic.Pago', 13)}${izq('Efectivo', 11)}` +
      `${izq('Cheque', 11)}OBSERVACIONES`,
  });
  l.push({ texto: '_'.repeat(A) });
  l.push({ texto: raya });
  let total = 0;
  const porTipo = new Map<string, number>();
  for (const n of notas) {
    const ds = docs.get(n.notaId) ?? [];
    const filas = ds.length
      ? ds.map((d) => ({
          td: d.tipoDte ? (TD_RANDOM[d.tipoDte] ?? String(d.tipoDte)) : 'NVV',
          numero: (d.folio ?? '').padStart(10, '0'),
          monto: Number(d.monto ?? 0),
        }))
      : [{ td: 'NVV', numero: n.numeroVisible, monto: 0 }];
    for (const f of filas) {
      total += f.monto;
      porTipo.set(f.td, (porTipo.get(f.td) ?? 0) + 1);
      l.push({
        texto:
          `${izq(f.td, 4)}${izq(f.numero, 11)}${izq(n.clienteRut ?? '', 10)} ` +
          `${izq(n.cliente.toUpperCase(), 29)} ${izq((n.direccionEntrega ?? '').toUpperCase(), 21)} ` +
          `${der(f.monto ? miles(f.monto) : '', 11)}  1 ${' '.repeat(18)}` +
          `${izq(n.condicionPago.toUpperCase(), 13)}.......... .......... ..............`,
      });
      l.push({ texto: ' ' });
    }
  }
  l.push({ texto: raya });
  l.push({ texto: `${izq('Totales :', 15)}${der(miles(total), 10)}` });
  l.push({ texto: `${izq('NVV Tot. :', 15)}${der(String(porTipo.get('NVV') ?? 0), 10)}` });
  for (const [td, k] of [...porTipo].filter(([td]) => td !== 'NVV'))
    l.push({ texto: `${izq(`${td} Tot. :`, 15)}${der(String(k), 10)}` });
  l.push({
    texto: `${izq('Clientes :', 15)}${der(String(new Set(notas.map((n) => n.clienteEntidadId)).size), 10)}`,
  });
  return l;
}
